import { ChatMessage, StepMessage } from '../chatMessage'

// 'message' groups exclude step senders by construction — the reducer routes
// step messages into 'steps' groups. Encoding that in the type lets consumers
// drop the `sender as 'user' | 'assistant'` cast at the AssistantChat call
// site.
export type NonStepChatMessage = ChatMessage & { sender: 'user' | 'assistant' }

export type MessageGroup =
  | { type: 'steps'; steps: StepMessage[]; key: string }
  | { type: 'message'; message: NonStepChatMessage }

/**
 * Collapses consecutive step messages into a single group so the chat view can
 * render them as an accordion. Non-step messages pass through as individual
 * 'message' groups. The group's key is the id of the first step in the run.
 */
export default function groupMessagesBySteps(messages: ChatMessage[]): MessageGroup[] {
  return messages.reduce<MessageGroup[]>((acc, msg) => {
    if (msg.sender === 'step') {
      const last = acc[acc.length - 1]
      if (last && last.type === 'steps') {
        // Replace the trailing group with a fresh object instead of mutating
        // last.steps in place. Mutation would land in the returned array, and
        // any consumer holding a reference to a previously returned group
        // could observe the steps array growing — surprising under React
        // concurrent rendering or in tests that compare snapshots over time.
        acc[acc.length - 1] = { ...last, steps: [...last.steps, msg as StepMessage] }
      } else {
        acc.push({ type: 'steps', steps: [msg as StepMessage], key: msg.id })
      }
    } else {
      // Narrowed by the explicit 'step' check above — sender is 'user' | 'assistant'.
      acc.push({ type: 'message', message: msg as NonStepChatMessage })
    }
    return acc
  }, [])
}
